import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';

import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import { allTravelDnaSteps } from '@/_constants/TravelDnaStep.ts';
import TravelDnaStep from '@/_constants/TravelDnaStep.ts';
import { dnaSchema } from '$/requests/UpsertDnaRequest.ts';
import { allHowDoYouLikeToExplore } from '$/_const/dna/HowDoYouLikeToExplore.ts';
import type { HowDoYouLikeToExplore } from '$/_const/dna/HowDoYouLikeToExplore.ts';
import type { TravelDnaFormData } from '$/models/Dna.d.ts';
import { missingTranslationToReadable } from '@/utils/missingTranslationToReadable.ts';
import type { OnCompleteArgs } from '@/components/functional/TravelDnaManipulationForm/TravelDnaManipulationForm.tsx';
import DnaStepLayout from '@/components/functional/TravelDnaManipulationForm/shared/DnaStepLayout.tsx';
import DnaImageChoiceCard from '@/components/functional/TravelDnaManipulationForm/shared/DnaImageChoiceCard.tsx';
import DnaError from '@/components/functional/TravelDnaManipulationForm/shared/DnaError.tsx';

import exploreWithAGuideImage from '@/assets/img/onboarding/plans/explore_with_a_guide.jpg';
import letTheDayUnfoldImage from '@/assets/img/onboarding/plans/let_the_day_unfold.jpg';
import seeTheMustSeesImage from '@/assets/img/onboarding/plans/see_the_must_sees.jpg';
import showMeTheLocalSideImage from '@/assets/img/onboarding/plans/show_me_the_local_side.jpg';

const yourWorldSchema = dnaSchema.pick({
  how_do_you_like_to_explore: true,
});

export type YourWorldSchema = z.infer<typeof yourWorldSchema>;

type YourWorldProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
};

const worldImages: Record<HowDoYouLikeToExplore, string> = {
  let_the_day_unfold: letTheDayUnfoldImage,
  show_me_the_local_side: showMeTheLocalSideImage,
  see_the_must_sees: seeTheMustSeesImage,
  explore_with_a_guide: exploreWithAGuideImage,
};

function YourWorld({ onComplete, onCompleteWhenDirty }: YourWorldProps) {
  const { t } = useTranslation();
  const { nextStep, setContextData, contextData } = useStepperContext<
    typeof allTravelDnaSteps,
    TravelDnaFormData
  >();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const selectedValue = contextData?.how_do_you_like_to_explore;

  const selectOption = (option: HowDoYouLikeToExplore) => {
    void (async () => {
      try {
        if (isPending) return;
        setIsPending(true);
        setError(null);
        const data = yourWorldSchema.parse({
          how_do_you_like_to_explore: option,
        });
        const context = { ...contextData, ...data };
        const args: OnCompleteArgs = {
          step: TravelDnaStep.YOUR_WORLD,
          data,
          context,
        };

        if (selectedValue !== option) {
          setContextData((previous) => ({ ...previous, ...data }));
          await onCompleteWhenDirty?.(args);
        }

        await onComplete?.(args);
        nextStep();
      } catch (caughtError) {
        setError(
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError))
        );
      } finally {
        setIsPending(false);
      }
    })();
  };

  return (
    <DnaStepLayout
      eyebrow={t('travelDnaPage.yourWorld.header')}
      title={t('travelDnaPage.yourWorld.subheader')}
      description={t('travelDnaPage.yourWorld.description')}
    >
      <DnaError error={error} className="mt-6" />
      <div className="mt-6 grid w-full grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-x-12 sm:gap-y-7">
        {allHowDoYouLikeToExplore.map((option) => {
          const selected = selectedValue === option;
          return (
            <DnaImageChoiceCard
              key={option}
              image={worldImages[option]}
              label={t(
                `travelDnaPage.yourWorld.options.${option}.title`,
                missingTranslationToReadable(option)
              )}
              description={t(
                `travelDnaPage.yourWorld.options.${option}.description`
              )}
              selected={selected}
              dimmed={Boolean(selectedValue) && !selected}
              disabled={isPending}
              onClick={() => selectOption(option)}
            />
          );
        })}
      </div>
    </DnaStepLayout>
  );
}

export default YourWorld;
